import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from '../Footer/Footer';
import Agendamento from '../Agendamento/Agendamento';
import Anamnese from '../Anamnese/Anamnese';
import AvaliacaoFisica from '../AvaliacaoFisica/AvaliacaoFisica';
import FormPaciente from '../Pacientes/FormPaciente';
import AlterarSenha from '../../pages/AlterarSenha/AlterarSenha';
import AlterarSenhaObrigatoria from '../../pages/AlterarSenhaObrigatoria/AlterarSenhaObrigatoria';
import { apiService } from '../../services/api';
import { RefreshCw } from 'lucide-react';
import './Dashboard.css';

interface User {
  role: 'paciente' | 'nutricionista';
  name: string;
  email: string;
  id?: string;
  avatar?: string;
}

interface DashboardProps {
  user: User;
  onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  const [currentPage, setCurrentPage] = useState('inicio');
  const [proximaConsulta, setProximaConsulta] = useState<string | null>(null);
  const [consultaPendente, setConsultaPendente] = useState<string | null>(null);
  const [showAlterarSenha, setShowAlterarSenha] = useState(false);
  const [temSenhaPadrao, setTemSenhaPadrao] = useState(false);
  const [verificandoSenha, setVerificandoSenha] = useState(true);

  useEffect(() => {
    verificarSenhaPadrao();
    if (user.role === 'paciente') {
      carregarProximaConsulta();
    }
  }, [user]);

  const verificarSenhaPadrao = async () => {
    try {
      const response = await apiService.verificarSenhaPadraoTemp(user.email);
      setTemSenhaPadrao(response.isSenhaPadrao);
    } catch (error) {
      console.error('Erro ao verificar senha padrão:', error);
    } finally {
      setVerificandoSenha(false);
    }
  };

  const carregarProximaConsulta = async () => {
    try {
      const consultas = await apiService.getConsultas();
      const agora = new Date();
      
      // Filtrar consultas do paciente
      const consultasPaciente = consultas.filter((consulta: any) => {
        return consulta.paciente?.email === user.email || 
               consulta.paciente?.nome === user.name ||
               consulta.pacienteNome === user.name;
      });
      
      // Verificar consulta pendente
      const pendente = consultasPaciente.find((consulta: any) => 
        (consulta.status === 'pendente' || consulta.status === 'agendada' || !consulta.status) &&
        new Date(consulta.dataHora) > agora
      );
      
      if (pendente) {
        const dataPendente = new Date(pendente.dataHora);
        const dataFormatada = dataPendente.toLocaleDateString('pt-BR');
        const horaFormatada = dataPendente.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        setConsultaPendente(`${dataFormatada} às ${horaFormatada}`);
      } else {
        setConsultaPendente(null);
      }
      
      // Filtrar consultas aprovadas futuras
      const consultasAprovadas = consultasPaciente
        .filter((consulta: any) => {
          const isFutura = new Date(consulta.dataHora) > agora;
          return consulta.status === 'aprovada' && isFutura;
        })
        .sort((a: any, b: any) => new Date(a.dataHora).getTime() - new Date(b.dataHora).getTime());
      
      if (consultasAprovadas.length > 0) {
        const dataConsulta = new Date(consultasAprovadas[0].dataHora);
        const dataFormatada = dataConsulta.toLocaleDateString('pt-BR');
        const horaFormatada = dataConsulta.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        setProximaConsulta(`${dataFormatada} às ${horaFormatada}`);
      } else {
        setProximaConsulta(null);
      }
    } catch (error) {
      console.error('Erro ao carregar próxima consulta:', error);
    }
  };

  const handleRefresh = () => {
    if (currentPage === 'inicio') {
      carregarProximaConsulta();
    }
    // Para outras páginas, força re-render
    const page = currentPage;
    setCurrentPage('');
    setTimeout(() => setCurrentPage(page), 10);
  };

  const renderConsultaInfo = () => {
    if (consultaPendente) {
      return (
        <>
          <div className="card-date text-danger">{consultaPendente}</div>
          <small className="text-danger">Aguardando confirmação</small>
        </>
      );
    }
    
    if (proximaConsulta) {
      return (
        <>
          <div className="card-date">{proximaConsulta}</div>
          <small>Consulta confirmada</small>
        </>
      );
    }
    
    return (
      <>
        <div className="card-date">-----</div>
        <small>Nenhuma consulta agendada</small>
      </>
    );
  };

  const renderContent = () => {
    if (showAlterarSenha) {
      return (
        <AlterarSenha 
          onComplete={() => setShowAlterarSenha(false)}
          onCancel={() => setShowAlterarSenha(false)}
        />
      );
    }

    switch (currentPage) {
      case 'agendamento':
        return <Agendamento user={user} />;
      case 'anamnese':
        return <Anamnese />;
      case 'avaliacao-fisica':
        return <AvaliacaoFisica />;
      case 'meus-dados':
        return (
          <FormPaciente 
            pacienteId={user.id}
            onVoltar={() => setCurrentPage('inicio')}
            onSalvar={() => setCurrentPage('inicio')}
          />
        );
      default:
        return (
          <div className="dashboard-content">
            <div className="welcome-banner">
              <h2>Olá, {user.name}! 🌱</h2>
              <p>Bem-vinda ao seu painel nutricional. Aqui você pode acompanhar suas consultas, planos alimentares e evolução do seu acompanhamento.</p>
            </div>
            
            <div className="dashboard-cards">
              <div className="dashboard-card">
                <h3>Próxima Consulta</h3>
                {renderConsultaInfo()}
              </div>
            </div>
          </div>
        );
    }
  };

  const renderMainContent = () => {
    if (verificandoSenha) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
          <output className="spinner-border text-success">
            <span className="visually-hidden">Verificando...</span>
          </output>
        </div>
      );
    }

    if (temSenhaPadrao) {
      return (
        <AlterarSenhaObrigatoria 
          userEmail={user.email}
          onComplete={() => setTemSenhaPadrao(false)}
          onCancel={onLogout}
        />
      );
    }

    return (
      <>
        <Sidebar currentPage={currentPage} onPageChange={setCurrentPage} user={user} />
        <div className="dashboard-main">
          <Header 
            currentPage={currentPage} 
            user={user} 
            onLogout={onLogout} 
            onPageChange={setCurrentPage}
            onAlterarSenha={() => setShowAlterarSenha(true)}
          />
          <div className="dashboard-content-wrapper">
            {renderContent()}
            <div className="d-flex justify-content-center my-4">
              <button 
                className="btn btn-outline-success rounded-circle p-3"
                onClick={handleRefresh}
                title="Atualizar"
              >
                <RefreshCw size={20} />
              </button>
            </div>
            <Footer />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="dashboard">
      {renderMainContent()}
    </div>
  );
};

export default Dashboard;